/**
 * Compact Development policy — fetch inputs from Supabase and compute time series
 * for the Policy Tool. Mirrors policy/compact_development.py worksheet logic.
 *
 * Worksheet structure (per city c, year t):
 * 1. vmt_below_state = 1 - (city_vmt_per_capita / state_vmt_per_capita)
 * 2. annual_growth_rate = (BAU growth % + vmt_below_state) / 100 by default (keep with population growth + VMT advantage); or user override (0–2%) / 100
 * 3. new_units_policy = annual_growth_rate * housing_units_bau; new_units_additional = new_units_policy - new_units_bau
 * 4. ghg_per_home_city = city_ldv_ghg / housing_units_bau
 * 5. ghg_per_home_compact = location-specific: (city_vmt_pc / state_vmt_pc) * ghg_per_home_state when city < state; else ghg_per_home_state (no savings)
 * 6. ghg_per_home_state = state_ldv_ghg / state_housing_units
 * 7. ghg_savings_per_home = ghg_per_home_state - ghg_per_home_compact (0 when city >= state)
 * 8. density_savings_annual: density increase % = new housing / existing stock (same as pop growth).
 *    Each 1% density increase → 0.22% less city-wide VMT. Savings = city_ldv_ghg × (density_increase_fraction) × 0.22.
 * 9–10. added_local_ghg and out_of_boundary_savings: use new_units_additional (incremental_baseline, preferred "beyond baseline")
 *       or new_units_policy (workbook_exact). Same-year units used for each year (no prior-year chaining).
 * 11. net_local_annual = density_savings_annual - added_local_ghg_annual; net_statewide_annual = oob + density - added_local
 * 12. Cumulatives: explicit sum of annual values (no recursive prior-year dependency).
 * Assumptions: 1:1 displacement (each new home here replaces one in average CA). Density elasticity: 1% density increase → 0.22% less city-wide VMT.
 */

import { resolveByGeofips } from '../domains/ghgInventoryReport/crosswalk';
import {
  LIVE_CHART_SECTOR_LABELS,
  extractSectorValue,
} from './ghgInventoryCalculationService';
import {
  dedupeReleaseRowsByCanonYear,
  fetchCommunityGhgReleaseRows,
} from './communityGhgInventoryReleaseService';
import { seriesValueAt } from './ghgInventoryEvidence';
import { fetchLiveInventoryCached } from './liveGhgPlaceSnapshot';
import { supabaseClient } from './supabase';

const STATE_GEOFIPS = 6;
const CA_COUNTY_GEOFIPS_MIN = 6000;
const CA_COUNTY_GEOFIPS_MAX = 7000;
/** Density elasticity for citywide effect (worksheet: -0.22). */
const DENSITY_ELASTICITY = -0.22;
/** Minimum population density (persons per sq mi) to recommend Compact Development, in addition to VMT < state. */
export const COMPACT_DEVELOPMENT_MIN_DENSITY = 3000;

function safeDiv(a: number, b: number, def = 0): number {
  if (b == null || !Number.isFinite(b) || b === 0) return def;
  if (a == null || !Number.isFinite(a)) return def;
  return a / b;
}

/** Single-year output from compact development worksheet (all fields exposed for charts and QA). */
export interface CompactDevelopmentYearRow {
  year: number;
  housingUnitsBau: number;
  newUnitsBau: number;
  newUnitsPolicy: number;
  newUnitsAdditional: number;
  cityVmtPerCapita: number;
  stateVmtPerCapita: number;
  vmtBelowState: number;
  annualGrowthRate: number;
  ghgPerHomeCity: number;
  ghgPerHomeCompact: number;
  ghgPerHomeState: number;
  ghgSavingsPerHome: number;
  /** City LDV GHG (BAU / baseline). For chart: city with policy = cityLdvGhg + netLocalAnnual (netLocal = local added − density avoided). */
  cityLdvGhg: number;
  /** State LDV GHG (BAU / baseline). For chart: state with policy = stateLdvGhg + netStatewideAnnual (net = statewide avoided + local added − density avoided). */
  stateLdvGhg: number;
  densitySavingsAnnual: number;
  /** Statewide avoided = units above BAU × state avg GHG per home (negative = avoided emissions). */
  statewideAvoidedAnnual: number;
  outOfBoundarySavingsAnnual: number;
  addedLocalGhgAnnual: number;
  netLocalAnnual: number;
  netStatewideAnnual: number;
  densitySavingsCumulative: number;
  statewideAvoidedCumulative: number;
  outOfBoundarySavingsCumulative: number;
  addedLocalGhgCumulative: number;
  netLocalCumulative: number;
  netStatewideCumulative: number;
}

/**
 * Rows use: state LDV with policy ≈ state LDV BAU + netStatewideAnnual, so negative net = GHG savings.
 * Policy impact / VMT UI expect a positive MT CO₂e reduction.
 */
export function ldvReductionMtFromNetStatewideAnnual(net: number | null | undefined): number {
  if (net == null || !Number.isFinite(net)) return 0;
  return Math.max(0, -net);
}

export interface CompactDevelopmentSeries {
  rows: CompactDevelopmentYearRow[];
  mode: 'workbook_exact' | 'incremental_baseline';
  /** True when location's VMT per capita is below state average and (density > threshold or density unknown). */
  recommendedForLocation: boolean;
  /** Latest population density (persons per sq mi) from population_density table, or null if not available. */
  populationDensityPerSqMi: number | null;
  /** True when recommendation is based on VMT only because density data were missing for this geography. */
  densityDataMissing?: boolean;
  /** Default percent growth per year = BAU growth % + vmt_below_state (fraction), capped at 3. Used as slider default. */
  defaultGrowthRatePercent: number;
  /** VMT per capita percent below state average (0–100 when below; 0 when at or above). For notification. */
  vmtPercentBelowState: number;
  /** VMT per capita percent above state average (0+ when above; 0 when at or below). For notification. */
  vmtPercentAboveState: number;
  /** BAU annual growth % (to keep with population growth), used in default = BAU% + (% below state). */
  bauGrowthPercent: number;
}

/** Try primary geofips; if 5/6 digit place code, also try the other form. Geofips is from the population table (same source as rest of app). */
function placeGeofipsToTry(geofips: number): number[] {
  const tryGeofips: number[] = [geofips];
  if (geofips >= 60000 && geofips <= 69999) tryGeofips.push(geofips + 540000);
  else if (geofips >= 600000 && geofips <= 699999) tryGeofips.push(geofips - 540000);
  return tryGeofips;
}

/** geography_type values to try for city/place in housing_to_employees (DB may use place, Place, or City). */
const HOUSING_PLACE_TYPES = ['place', 'Place', 'City'] as const;

/**
 * Fetch city housing units by year (place). Tries alternate 5/6-digit geofips and geography_type (place, Place, City) if primary returns no rows.
 */
async function fetchCityHousing(geofips: number, years: number[]): Promise<{ year: number; housing_units: number }[]> {
  for (const g of placeGeofipsToTry(geofips)) {
    for (const geoType of HOUSING_PLACE_TYPES) {
      const { data, error } = await supabaseClient
        .from('housing_to_employees')
        .select('year, housing_units')
        .eq('geofips', g)
        .eq('geography_type', geoType)
        .in('year', years)
        .order('year', { ascending: true });
      if (!error && data && data.length > 0) return data as { year: number; housing_units: number }[];
    }
  }
  return [];
}

/**
 * Fetch city LDV emissions and VMT from fetchGHGInventoryData (live GHG page).
 */
async function fetchCityGhg(geofips: number, years: number[]): Promise<{ year: number; ldv_emissions: number; ldv_vmt: number }[]> {
  const crosswalk = resolveByGeofips(geofips);
  if (!crosswalk) return [];
  const locationName = crosswalk.populationName || crosswalk.capName;
  const result = await fetchLiveInventoryCached(locationName);
  const out: { year: number; ldv_emissions: number; ldv_vmt: number }[] = [];
  for (const year of years) {
    const ldv = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.LDV, year);
    const vmt = seriesValueAt(result.inventoryEvidence?.ldvVmt, year);
    if (ldv == null || vmt == null) continue;
    out.push({ year, ldv_emissions: ldv, ldv_vmt: vmt });
  }
  return out;
}

/**
 * Fetch city population. Tries alternate 5/6-digit geofips if primary returns no rows.
 */
async function fetchCityPopulation(geofips: number, years: number[]): Promise<{ year: number; population: number }[]> {
  for (const g of placeGeofipsToTry(geofips)) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', g)
      .in('year', years)
      .order('year', { ascending: true });
    if (!error && data && data.length > 0) return data as { year: number; population: number }[];
  }
  return [];
}

/**
 * Fetch latest population density (persons per sq mi) by geofips from population_density table.
 * Geofips is the same as used elsewhere in the app (from the population table via getGeofips).
 * Lookup is by geofips only; geography_type is implicit and not filtered.
 */
async function fetchLatestPopulationDensity(geofips: number, locationName?: string): Promise<number | null> {
  const toTry = placeGeofipsToTry(geofips);
  for (const g of toTry) {
    const { data, error } = await supabaseClient
      .from('population_density')
      .select('population_density')
      .eq('geofips', g)
      .order('year', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      continue; // try next geofips
    }
    if (data != null) {
      const d = Number((data as { population_density?: number | null }).population_density);
      if (Number.isFinite(d)) {
        return d;
      }
    }
  }
  return null;
}

/**
 * Fetch state housing units (geofips=6, state).
 */
async function fetchStateHousing(years: number[]): Promise<{ year: number; housing_units: number }[]> {
  const { data, error } = await supabaseClient
    .from('housing_to_employees')
    .select('year, housing_units')
    .eq('geofips', STATE_GEOFIPS)
    .eq('geography_type', 'state')
    .in('year', years)
    .order('year', { ascending: true });
  if (error) return [];
  return (data ?? []) as { year: number; housing_units: number }[];
}

/** State LDV and VMT = sum of published release CA county rows (not live per-county calc). */
async function fetchStateLdvVmt(years: number[]): Promise<{ year: number; ldv_emissions: number; ldv_vmt: number }[]> {
  const yearMin = Math.min(...years);
  const yearMax = Math.max(...years);
  const { rows, error } = await fetchCommunityGhgReleaseRows({
    geographyType: 'County',
    yearMin,
    yearMax,
    select: 'geofips,year,calculation_date,ldv_emissions,ldv_vmt',
  });
  if (error) return [];
  const byYear: Record<number, { ldv_emissions: number; ldv_vmt: number }> = {};
  for (const year of years) {
    byYear[year] = { ldv_emissions: 0, ldv_vmt: 0 };
  }
  for (const r of dedupeReleaseRowsByCanonYear(rows).values()) {
    if (!byYear[r.year]) continue;
    if (r.ldv_emissions != null) byYear[r.year].ldv_emissions += r.ldv_emissions;
    if (r.ldv_vmt != null) byYear[r.year].ldv_vmt += r.ldv_vmt;
  }
  return Object.entries(byYear).map(([y, v]) => ({
    year: Number(y),
    ldv_emissions: v.ldv_emissions,
    ldv_vmt: v.ldv_vmt,
  }));
}

/**
 * State population (geofips=6).
 */
async function fetchStatePopulation(years: number[]): Promise<{ year: number; population: number }[]> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('year, population')
    .eq('geofips', STATE_GEOFIPS)
    .in('year', years)
    .order('year', { ascending: true });
  if (error) return [];
  return (data ?? []) as { year: number; population: number }[];
}

/** Cap for percent growth per year (slider max). */
export const GROWTH_RATE_PERCENT_MAX = 3;

/** Default BAU annual housing growth rate (decimal) for projecting when housing_to_employees ends at 2023. Matches Excel "1% by default". */
const DEFAULT_BAU_HOUSING_GROWTH_RATE = 0.01;

/**
 * Fetch housing units by year for a geofips (residential stock proxy).
 * Projects to endYear when DB ends earlier. Used by building electrification cost layer.
 */
export async function fetchHousingUnitsByYear(
  geofips: number,
  startYear: number,
  endYear: number
): Promise<Record<number, number>> {
  if (!Number.isFinite(geofips) || !Number.isFinite(startYear) || !Number.isFinite(endYear)) {
    const years = Array.from({ length: Math.max(0, endYear - startYear + 1) }, (_, i) => startYear + i);
    return Object.fromEntries(years.map((y) => [y, 0]));
  }
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  const cityHousing = await fetchCityHousing(geofips, years);
  const byYear: Record<number, number> = {};
  for (const y of years) byYear[y] = 0;
  for (const r of cityHousing) {
    if (byYear[r.year] !== undefined) byYear[r.year] = Number(r.housing_units) || 0;
  }
  const sortedYears = [...years].sort((a, b) => a - b);
  let lastKnownYear = sortedYears[0];
  let lastKnownVal = 0;
  for (let i = sortedYears.length - 1; i >= 0; i--) {
    const y = sortedYears[i];
    if (byYear[y] > 0) {
      lastKnownYear = y;
      lastKnownVal = byYear[y];
      break;
    }
  }
  let bauGrowth = DEFAULT_BAU_HOUSING_GROWTH_RATE;
  const prevYear = lastKnownYear - 1;
  if (prevYear >= startYear && byYear[prevYear] > 0 && lastKnownVal >= byYear[prevYear]) {
    const implied = (lastKnownVal - byYear[prevYear]) / byYear[prevYear];
    if (Number.isFinite(implied) && implied >= 0 && implied <= 0.05) bauGrowth = implied;
  }
  for (const y of sortedYears) {
    if (y <= lastKnownYear) continue;
    const prev = byYear[y - 1] ?? lastKnownVal;
    byYear[y] = prev > 0
      ? prev * (1 + bauGrowth)
      : lastKnownVal * Math.pow(1 + bauGrowth, y - lastKnownYear);
  }
  return byYear;
}

/**
 * Compute compact development time series following worksheet logic.
 * Mode: workbook_exact uses new_units_policy for oob and added_local; incremental_baseline uses new_units_additional.
 * When growthRatePercentOverride is provided (0–2), use it as annual_growth_rate = override/100 for all years; otherwise use (BAU growth % + vmt_below_state) / 100 per year.
 */
function computeSeries(
  merged: {
    year: number;
    housing_units_bau: number;
    city_ldv_ghg: number;
    city_ldv_vmt: number;
    city_pop: number;
    state_housing_units: number;
    state_ldv_ghg: number;
    state_ldv_vmt: number;
    state_pop: number;
  }[],
  mode: 'workbook_exact' | 'incremental_baseline' = 'incremental_baseline',
  growthRatePercentOverride?: number
): CompactDevelopmentYearRow[] {
  const sorted = [...merged].sort((a, b) => a.year - b.year);
  const out: CompactDevelopmentYearRow[] = [];
  let densityCum = 0;
  let stateAvoidedCum = 0;
  let oobCum = 0;
  let addedCum = 0;
  let netLocalCum = 0;
  let netStatewideCum = 0;

  for (let i = 0; i < sorted.length; i++) {
    const r = sorted[i];
    const prevHousing = i > 0 ? sorted[i - 1].housing_units_bau : r.housing_units_bau;
    const newUnitsBau = Math.max(0, (r.housing_units_bau ?? 0) - prevHousing);
    // (1) Relative VMT advantage (fraction 0–1, e.g. 0.37 = 37% below state)
    const cityVmtPc = safeDiv(r.city_ldv_vmt, r.city_pop);
    const stateVmtPc = safeDiv(r.state_ldv_vmt, r.state_pop);
    const vmtBelowState =
      stateVmtPc > 0 ? Math.max(0, 1 - cityVmtPc / stateVmtPc) : 0;
    // (2) Policy housing growth rate: default = (% to keep with population/BAU growth) + (% below state as fraction), capped at 2%; or user override
    const bauGrowthPercent =
      prevHousing > 0 && Number.isFinite(newUnitsBau / prevHousing)
        ? 100 * (newUnitsBau / prevHousing)
        : 0;
    const defaultGrowthPercent = Math.min(
      GROWTH_RATE_PERCENT_MAX,
      Math.max(0, bauGrowthPercent + vmtBelowState)
    );
    const annualGrowthRate =
      growthRatePercentOverride != null && Number.isFinite(growthRatePercentOverride)
        ? Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(0, growthRatePercentOverride)) / 100
        : defaultGrowthPercent / 100;
    // (3) Housing under policy
    const newUnitsPolicy = annualGrowthRate * (r.housing_units_bau ?? 0);
    const newUnitsAdditional = newUnitsPolicy - newUnitsBau;
    // (4)–(7) Per-home emissions and savings (location-specific: use city vs state VMT ratio, not fixed 29%/70%)
    const ghgPerHomeCity = safeDiv(r.city_ldv_ghg, r.housing_units_bau);
    const ghgPerHomeState = safeDiv(r.state_ldv_ghg, r.state_housing_units);
    // Compact homes in this city: assume they drive at this city's VMT rate. Savings vs state only when city < state.
    const ghgPerHomeCompact =
      stateVmtPc > 0 && cityVmtPc < stateVmtPc
        ? (cityVmtPc / stateVmtPc) * ghgPerHomeState
        : ghgPerHomeState;
    const ghgSavingsPerHome = Math.max(0, ghgPerHomeState - ghgPerHomeCompact);
    // (8) Density effect: density increase % = new housing / existing stock (same as pop growth, same people per household).
    // Each 1% density increase → 0.22% decrease in city-wide VMT. Savings = city_ldv_ghg × (density_increase) × 0.22.
    const housingStock = r.housing_units_bau ?? 0;
    const densityIncreaseFraction =
      housingStock > 0
        ? (mode === 'incremental_baseline'
            ? newUnitsAdditional / housingStock
            : newUnitsPolicy / housingStock)
        : 0;
    const densitySavingsAnnual =
      (r.city_ldv_ghg ?? 0) * densityIncreaseFraction * Math.abs(DENSITY_ELASTICITY);
    // (9)–(10) Beyond-baseline: use additional units (row 14). Same-year units only (no prior-year ref).
    const nOob = mode === 'workbook_exact' ? newUnitsPolicy : newUnitsAdditional;
    const nLocal = mode === 'workbook_exact' ? newUnitsPolicy : newUnitsAdditional;
    const outOfBoundarySavingsAnnual = nOob * ghgSavingsPerHome;
    const addedLocalGhgAnnual = nLocal * ghgPerHomeCompact;
    // Statewide avoided = units above BAU × state avg GHG per home (negative = avoided emissions)
    const statewideAvoidedAnnual = -(nOob * ghgPerHomeState);
    // (11) Net annual: Statewide net = Statewide avoided + local added − density avoided; Local net = local added − density avoided
    const netLocalAnnual = addedLocalGhgAnnual - densitySavingsAnnual;
    const netStatewideAnnual =
      statewideAvoidedAnnual + addedLocalGhgAnnual - densitySavingsAnnual;
    // (12) Cumulatives
    densityCum += densitySavingsAnnual;
    stateAvoidedCum += statewideAvoidedAnnual;
    oobCum += outOfBoundarySavingsAnnual;
    addedCum += addedLocalGhgAnnual;
    netLocalCum += netLocalAnnual;
    netStatewideCum += netStatewideAnnual;

    out.push({
      year: r.year,
      housingUnitsBau: r.housing_units_bau ?? 0,
      newUnitsBau,
      newUnitsPolicy,
      newUnitsAdditional,
      cityVmtPerCapita: cityVmtPc,
      stateVmtPerCapita: stateVmtPc,
      vmtBelowState,
      annualGrowthRate,
      ghgPerHomeCity,
      ghgPerHomeCompact,
      ghgPerHomeState,
      ghgSavingsPerHome,
      cityLdvGhg: r.city_ldv_ghg ?? 0,
      stateLdvGhg: r.state_ldv_ghg ?? 0,
      densitySavingsAnnual,
      statewideAvoidedAnnual,
      outOfBoundarySavingsAnnual,
      addedLocalGhgAnnual,
      netLocalAnnual,
      netStatewideAnnual,
      densitySavingsCumulative: densityCum,
      statewideAvoidedCumulative: stateAvoidedCum,
      outOfBoundarySavingsCumulative: oobCum,
      addedLocalGhgCumulative: addedCum,
      netLocalCumulative: netLocalCum,
      netStatewideCumulative: netStatewideCum,
    });
  }
  return out;
}

/**
 * Fetch all inputs and return compact development time series for charts.
 */
export async function fetchCompactDevelopmentSeries(
  geofips: number,
  startYear: number,
  endYear: number,
  mode: 'workbook_exact' | 'incremental_baseline' = 'incremental_baseline',
  /** User override: percent growth per year (0–2). When set, used instead of formula (1 + vmt_below_state). */
  growthRatePercentOverride?: number,
  /** Optional; density is looked up by geofips only (same geofips as from population table). */
  locationName?: string
): Promise<CompactDevelopmentSeries | null> {
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);

  const [cityHousing, cityGhg, cityPop, stateHousing, stateLdv, statePop, populationDensityPerSqMi] = await Promise.all([
    fetchCityHousing(geofips, years),
    fetchCityGhg(geofips, years),
    fetchCityPopulation(geofips, years),
    fetchStateHousing(years),
    fetchStateLdvVmt(years),
    fetchStatePopulation(years),
    fetchLatestPopulationDensity(geofips, locationName),
  ]);

  const byYear: Record<
    number,
    {
      year: number;
      housing_units_bau: number;
      city_ldv_ghg: number;
      city_ldv_vmt: number;
      city_pop: number;
      state_housing_units: number;
      state_ldv_ghg: number;
      state_ldv_vmt: number;
      state_pop: number;
    }
  > = {};
  for (const y of years) {
    byYear[y] = {
      year: y,
      housing_units_bau: 0,
      city_ldv_ghg: 0,
      city_ldv_vmt: 0,
      city_pop: 0,
      state_housing_units: 0,
      state_ldv_ghg: 0,
      state_ldv_vmt: 0,
      state_pop: 0,
    };
  }
  for (const r of cityHousing) {
    if (byYear[r.year]) byYear[r.year].housing_units_bau = Number(r.housing_units) ?? 0;
  }
  for (const r of cityGhg) {
    if (byYear[r.year]) {
      byYear[r.year].city_ldv_ghg = Number(r.ldv_emissions) ?? 0;
      byYear[r.year].city_ldv_vmt = Number(r.ldv_vmt) ?? 0;
    }
  }
  for (const r of cityPop) {
    if (byYear[r.year]) byYear[r.year].city_pop = Number(r.population) ?? 0;
  }
  for (const r of stateHousing) {
    if (byYear[r.year]) byYear[r.year].state_housing_units = Number(r.housing_units) ?? 0;
  }
  for (const r of stateLdv) {
    if (byYear[r.year]) {
      byYear[r.year].state_ldv_ghg = Number(r.ldv_emissions) ?? 0;
      byYear[r.year].state_ldv_vmt = Number(r.ldv_vmt) ?? 0;
    }
  }
  for (const r of statePop) {
    if (byYear[r.year]) byYear[r.year].state_pop = Number(r.population) ?? 0;
  }

  // Project BAU housing and carry forward city/state GHG, VMT, population for years after last available (e.g. 2024-2045 when DB ends at 2023)
  const sortedYears = [...years].sort((a, b) => a - b);
  let lastIndexWithData = -1;
  for (let i = sortedYears.length - 1; i >= 0; i--) {
    const y = sortedYears[i];
    const r = byYear[y];
    if (r && (r.housing_units_bau > 0 || r.city_ldv_ghg > 0 || r.state_ldv_ghg > 0)) {
      lastIndexWithData = i;
      break;
    }
  }
  const lastKnownYear = lastIndexWithData >= 0 ? sortedYears[lastIndexWithData] : sortedYears[0];
  const lastKnownRow = byYear[lastKnownYear];
  // Use observed BAU growth from last two years of data for projection so 2024+ don't jump vs 2023
  const prevDataRow = lastKnownYear > 0 ? byYear[lastKnownYear - 1] : null;
  let bauGrowthForProjection = DEFAULT_BAU_HOUSING_GROWTH_RATE;
  if (prevDataRow && prevDataRow.housing_units_bau > 0 && lastKnownRow.housing_units_bau >= prevDataRow.housing_units_bau) {
    const implied = (lastKnownRow.housing_units_bau - prevDataRow.housing_units_bau) / prevDataRow.housing_units_bau;
    if (Number.isFinite(implied) && implied >= 0 && implied <= 0.05) bauGrowthForProjection = implied;
  }

  for (const y of sortedYears) {
    if (y <= lastKnownYear || !lastKnownRow) continue;
    const prev = byYear[y - 1];
    if (!prev) continue;
    // Project BAU housing: use same growth rate as last observed (so no discontinuity at 2024)
    byYear[y].housing_units_bau = prev.housing_units_bau > 0
      ? prev.housing_units_bau * (1 + bauGrowthForProjection)
      : lastKnownRow.housing_units_bau * Math.pow(1 + bauGrowthForProjection, y - lastKnownYear);
    // Carry forward city/state for projection years (no DB data)
    byYear[y].city_ldv_ghg = prev.city_ldv_ghg || lastKnownRow.city_ldv_ghg;
    byYear[y].city_ldv_vmt = prev.city_ldv_vmt || lastKnownRow.city_ldv_vmt;
    byYear[y].city_pop = prev.city_pop || lastKnownRow.city_pop;
    byYear[y].state_housing_units = prev.state_housing_units || lastKnownRow.state_housing_units;
    byYear[y].state_ldv_ghg = prev.state_ldv_ghg || lastKnownRow.state_ldv_ghg;
    byYear[y].state_ldv_vmt = prev.state_ldv_vmt || lastKnownRow.state_ldv_vmt;
    byYear[y].state_pop = prev.state_pop || lastKnownRow.state_pop;
  }

  const merged = years.map((y) => byYear[y]).filter((r) => r != null);
  if (merged.length === 0) return null;

  // Default percent growth per year = BAU growth % + (VMT below state as fraction), capped at 3%.
  const sortedForDefault = [...merged].sort((a, b) => a.year - b.year);
  const lastMerged = sortedForDefault[sortedForDefault.length - 1];
  const cityPc = lastMerged ? safeDiv(lastMerged.city_ldv_vmt, lastMerged.city_pop) : 0;
  const statePc = lastMerged ? safeDiv(lastMerged.state_ldv_vmt, lastMerged.state_pop) : 0;
  const vmtBelowStateLast = statePc > 0 ? Math.max(0, 1 - cityPc / statePc) : 0;
  const vmtAboveStateLast = statePc > 0 && cityPc >= statePc ? (cityPc / statePc - 1) : 0;
  const bauGrowthPercentLast = bauGrowthForProjection * 100;
  const defaultGrowthRatePercent = Math.min(
    GROWTH_RATE_PERCENT_MAX,
    Math.max(0, bauGrowthPercentLast + vmtBelowStateLast)
  );

  const rows = computeSeries(merged, mode, growthRatePercentOverride);
  // Recommend when VMT per capita is below state average AND (density > 3000 or density unknown).
  const lastRow = rows[rows.length - 1];
  const vmtRecommended =
    lastRow != null &&
    lastRow.stateVmtPerCapita > 0 &&
    lastRow.cityVmtPerCapita < lastRow.stateVmtPerCapita;
  const densityKnown =
    populationDensityPerSqMi != null && Number.isFinite(populationDensityPerSqMi);
  const densityOk = densityKnown && populationDensityPerSqMi! >= COMPACT_DEVELOPMENT_MIN_DENSITY;
  const densityDataMissing = vmtRecommended && !densityKnown;
  const recommendedForLocation = vmtRecommended && (densityOk || densityDataMissing);

  const vmtPercentBelowState = Math.round(vmtBelowStateLast * 1000) / 10;
  const vmtPercentAboveState = Math.round(vmtAboveStateLast * 1000) / 10;
  const bauGrowthPercent = Math.round(bauGrowthPercentLast * 100) / 100;

  return {
    rows,
    mode,
    recommendedForLocation,
    populationDensityPerSqMi: populationDensityPerSqMi ?? null,
    densityDataMissing: densityDataMissing || undefined,
    defaultGrowthRatePercent,
    vmtPercentBelowState,
    vmtPercentAboveState,
    bauGrowthPercent,
  };
}
